import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Camera, MapPin, Clock, ChevronRight, X, Layers } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  DRAFT: 'bg-slate-100 text-slate-600', IN_PROGRESS: 'bg-blue-100 text-blue-700',
  PROCESSING: 'bg-amber-100 text-amber-700', COMPLETED: 'bg-emerald-100 text-emerald-700',
  ARCHIVED: 'bg-slate-200 text-slate-500',
};

export default function SiteWalks() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [walks, setWalks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', captureMethod: 'PHONE_PANO' });

  const load = async () => {
    setLoading(true);
    try { setWalks((await axios.get(`${API}/projects/${projectId}/site-walks`)).data.items || []); }
    catch (e) { toast.error('Failed to load site walks'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const create = async () => {
    if (!form.name.trim()) return toast.error('Walk name required');
    try {
      const r = await axios.post(`${API}/projects/${projectId}/site-walks`, form);
      toast.success(`Created ${r.data.walkNumber}`);
      setCreating(false); setForm({ name: '', description: '', captureMethod: 'PHONE_PANO' });
      navigate(`/app/projects/${projectId}/site-walks/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/app/projects')} className="p-2 rounded-lg hover:bg-slate-100" data-testid="sitewalk-back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Camera className="w-6 h-6 text-sky-600" /> 360° Site Walks</h1>
              <p className="text-sm text-slate-500">360 site documentation — context-linked capture, not a geometric digital twin.</p>
            </div>
          </div>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 px-4 py-2 bg-sky-600 text-white rounded-lg text-sm hover:bg-sky-700" data-testid="sitewalk-create-btn"><Plus className="w-4 h-4" /> New Site Walk</button>
        </div>
        <div className="mb-6">
          <button onClick={() => navigate(`/app/projects/${projectId}/site-walk-ops`)} className="flex items-center gap-1.5 px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-900" data-testid="sitewalk-ops-btn"><Layers className="w-4 h-4" /> 360° Operations (Coverage · Floor Plan · Reports)</button>
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : walks.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center" data-testid="sitewalk-empty">
            <Camera className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-600 font-medium">No site walks yet</p>
            <p className="text-sm text-slate-400">Create a walk, add capture points and upload 360 panoramas.</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="sitewalk-list">
            {walks.map((w) => (
              <button key={w.id} onClick={() => navigate(`/app/projects/${projectId}/site-walks/${w.id}`)}
                className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:shadow-md transition-shadow flex items-center justify-between" data-testid={`sitewalk-row-${w.walkNumber}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-800">{w.walkNumber}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_STYLE[w.status] || 'bg-slate-100'}`}>{w.status}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">{w.name}</p>
                  <div className="flex items-center gap-4 text-[11px] text-slate-400 mt-1">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {w.capturePointCount || 0} points</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {(w.captureDate || '').slice(0, 10)}</span>
                    <span>{w.captureMethod}</span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300" />
              </button>
            ))}
          </div>
        )}
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="sitewalk-create-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-slate-800">New Site Walk</h3>
              <button onClick={() => setCreating(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <label className="text-xs font-medium text-slate-600">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" placeholder="e.g. Tower A · Floor 2" data-testid="sitewalk-name-input" />
            <label className="text-xs font-medium text-slate-600">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-3" rows={2} data-testid="sitewalk-desc-input" />
            <label className="text-xs font-medium text-slate-600">Capture method</label>
            <select value={form.captureMethod} onChange={(e) => setForm({ ...form, captureMethod: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1 mb-4" data-testid="sitewalk-method-select">
              <option value="PHONE_PANO">Phone panorama</option>
              <option value="EXTERNAL_360">External 360 camera</option>
              <option value="UPLOAD_BATCH">Batch upload</option>
              <option value="MIXED">Mixed</option>
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
              <button onClick={create} className="px-4 py-2 bg-sky-600 text-white rounded-lg text-sm" data-testid="sitewalk-create-submit">Create Walk</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
